// src/accreditation/access-windows.ts
function isDaily(w) {
  return typeof w.timeFrom === "string";
}
function parseWindows(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((w) => {
    if (!w || typeof w !== "object") return false;
    const daily = w;
    const abs = w;
    return typeof daily.timeFrom === "string" && typeof daily.timeTo === "string" || typeof abs.from === "string" && typeof abs.to === "string";
  });
}
function zoneOffsetMinutes(timeZone, at) {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    const parts = Object.fromEntries(dtf.formatToParts(at).map((p) => [p.type, p.value]));
    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour === "24" ? "00" : parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    return (asUtc - at.getTime()) / 6e4;
  } catch {
    return 0;
  }
}
function localMidnightUtc(at, timeZone) {
  const offset = zoneOffsetMinutes(timeZone, at);
  const local = new Date(at.getTime() + offset * 6e4);
  const midnightLocal = Date.UTC(
    local.getUTCFullYear(),
    local.getUTCMonth(),
    local.getUTCDate(),
    0,
    0,
    0
  );
  return new Date(midnightLocal - offset * 6e4);
}
function minutesOf(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59) return null;
  return h * 60 + min;
}
function findWindow(windows, at, timeZone) {
  if (windows.length === 0) {
    return { allowed: true, window: null };
  }
  for (const w of windows) {
    if (isDaily(w)) {
      const from = minutesOf(w.timeFrom);
      const to = minutesOf(w.timeTo);
      if (from === null || to === null) continue;
      const midnight = localMidnightUtc(at, timeZone).getTime();
      const start = new Date(midnight + from * 6e4);
      const end = new Date(midnight + (to > from ? to : to + 24 * 60) * 6e4);
      if (at >= start && at <= end) {
        return { allowed: true, window: { start, end } };
      }
      if (to <= from) {
        const yStart = new Date(start.getTime() - 24 * 36e5);
        const yEnd = new Date(end.getTime() - 24 * 36e5);
        if (at >= yStart && at <= yEnd) {
          return { allowed: true, window: { start: yStart, end: yEnd } };
        }
      }
    } else {
      const start = new Date(w.from);
      const end = new Date(w.to);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
      if (at >= start && at <= end) {
        return { allowed: true, window: { start, end } };
      }
    }
  }
  return { allowed: false, window: null };
}

// src/accreditation/badge-cells.ts
var BADGE_CELL_MIN_MM = 8;
var BADGE_CELL_MAX_MM = 10;
function pickBadgeCells(cells, options) {
  const pick = options.pick ?? "roles";
  if (pick === "manual") {
    const byKey2 = new Map(cells.map((c) => [c.key, c]));
    return (options.keys ?? []).map((key) => byKey2.get(key)).filter((c) => c !== void 0);
  }
  if (pick === "all" || options.roleServiceIds === null) {
    return [...cells];
  }
  const roles = options.roleServiceIds;
  return cells.filter((c) => c.serviceIds.some((id) => roles.has(id)));
}
function badgeCellGrid(count, box) {
  if (count <= 0) return { cell: 0, rows: 0, positions: [] };
  const { width, height, gap } = box;
  const maxCell = box.maxCell ?? Infinity;
  const cellFor = (rows2) => {
    const perRow2 = Math.ceil(count / rows2);
    return Math.min(
      (width - gap * (perRow2 - 1)) / perRow2,
      (height - gap * (rows2 - 1)) / rows2,
      maxCell
    );
  };
  let rows;
  const wanted = box.rows ?? 1;
  if (wanted === "auto") {
    rows = 1;
    let best = cellFor(1);
    for (let r = 1; r <= Math.min(3, count); r += 1) {
      const c = cellFor(r);
      if (c >= box.minCell) {
        rows = r;
        break;
      }
      if (c > best) {
        best = c;
        rows = r;
      }
    }
  } else {
    rows = Math.max(1, Math.min(wanted, count));
  }
  const cell = cellFor(rows);
  if (cell <= 0) return { cell: 0, rows, positions: [] };
  const perRow = Math.ceil(count / rows);
  const blockHeight = rows * cell + (rows - 1) * gap;
  const top = (height - blockHeight) / 2;
  const positions = [];
  for (let i = 0; i < count; i += 1) {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    const inRow = Math.min(perRow, count - row * perRow);
    const rowWidth = inRow * cell + (inRow - 1) * gap;
    const offset = box.align === "center" ? (width - rowWidth) / 2 : box.align === "right" ? width - rowWidth : 0;
    positions.push({ x: offset + col * (cell + gap), y: top + row * (cell + gap) });
  }
  return { cell, rows, positions };
}

// src/accreditation/badge-text.ts
var BADGE_TEXT_LINE_HEIGHT = 1.15;
var BADGE_TEXT_MIN_SIZE = 5;
var DESCENT = 0.25;
function layoutBadgeText(opts) {
  const parts = opts.parts.filter((p) => p.text.trim());
  if (parts.length === 0) return { lines: [], scale: 1, overflow: false };
  const maxLines = Math.max(1, Math.floor(opts.maxLines));
  const best = fitScale(parts, opts.width, opts.height, maxLines, opts.measure);
  for (let n = 1; n < maxLines; n++) {
    const fewer = fitScale(parts, opts.width, opts.height, n, opts.measure);
    if (!fewer.overflow && fewer.scale >= best.scale * (1 - WRAP_TOLERANCE)) return fewer;
  }
  return best;
}
var WRAP_TOLERANCE = 0.25;
function fitScale(parts, width, height, maxLines, measure) {
  const opts = { width, height, measure };
  const smallest = Math.min(...parts.map((p) => p.size));
  const minScale = Math.min(1, BADGE_TEXT_MIN_SIZE / smallest);
  let scale = 1;
  for (; ; ) {
    const block = arrange(parts, scale, opts.width, opts.measure);
    const fits = block.perPart.every((n) => n <= maxLines) && block.lines.every((l) => l.width <= opts.width + 0.01) && block.height <= opts.height + 0.01;
    if (fits) return { lines: block.lines, scale, overflow: false };
    if (scale <= minScale) return { lines: block.lines, scale, overflow: true };
    scale = Math.max(minScale, Math.round((scale - 0.01) * 100) / 100);
  }
}
function arrange(parts, scale, width, measure) {
  const lines = [];
  const perPart = [];
  let baseline = 0;
  let lastSize = 0;
  parts.forEach((part, index) => {
    const size = part.size * scale;
    const wrapped = wrapWords(part.text, (t) => measure(t, size, part.bold), width);
    perPart.push(wrapped.length);
    for (const text of wrapped) {
      baseline += lines.length === 0 ? size : size * BADGE_TEXT_LINE_HEIGHT;
      lines.push({ text, size, bold: part.bold, part: index, baseline, width: measure(text, size, part.bold) });
      lastSize = size;
    }
  });
  return { lines, perPart, height: baseline + lastSize * DESCENT };
}
function wrapWords(text, width, maxWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && width(candidate) > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}
function shrinkToWidth(measure, text, declared, maxWidth) {
  const MIN = 4;
  let size = declared;
  while (size > MIN && measure(text, size) > maxWidth) size -= 0.25;
  return size;
}

// src/assignments/sort.ts
var ASSIGNMENT_SORT_KEYS = ["number", "project", "executor", "due", "status"];
var STATUS_ORDER = ["DRAFT", "APPROVAL", "NEW", "IN_PROGRESS", "REWORK", "REVIEW", "DONE", "CANCELLED"];
var collator = new Intl.Collator("ru", { sensitivity: "base", numeric: true });
function nullsLast(x, y, cmp, dir) {
  if (x === null && y === null) return 0;
  if (x === null) return 1;
  if (y === null) return -1;
  return dir * cmp(x, y);
}
function byDefault(a, b) {
  return nullsLast(a.dueDate, b.dueDate, (x, y) => x.localeCompare(y), 1) || b.number - a.number;
}
function byKey(a, b, key, dir) {
  switch (key) {
    case "number":
      return dir * (a.number - b.number);
    case "project":
      return nullsLast(a.project?.name ?? null, b.project?.name ?? null, (x, y) => collator.compare(x, y), dir);
    case "executor":
      return nullsLast(
        a.executor ? `${a.executor.lastName} ${a.executor.firstName}` : null,
        b.executor ? `${b.executor.lastName} ${b.executor.firstName}` : null,
        (x, y) => collator.compare(x, y),
        dir
      );
    case "due":
      return nullsLast(a.dueDate, b.dueDate, (x, y) => x.localeCompare(y), dir);
    case "status": {
      const rank = (s) => {
        const i = STATUS_ORDER.indexOf(s.status);
        return i === -1 ? STATUS_ORDER.length : i;
      };
      return dir * (rank(a) - rank(b)) || Number(b.isOverdue) - Number(a.isOverdue);
    }
  }
}
function sortAssignments(rows, sort) {
  const dir = sort?.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => (sort ? byKey(a, b, sort.key, dir) : 0) || byDefault(a, b));
}
function isAssignmentSortKey(v) {
  return typeof v === "string" && ASSIGNMENT_SORT_KEYS.includes(v);
}

// src/auth/password-policy.ts
var PASSWORD_MIN_LENGTH = 8;
var RULES = [
  { issue: "length", test: (v) => v.length >= PASSWORD_MIN_LENGTH },
  { issue: "lower", test: (v) => /\p{Ll}/u.test(v) },
  { issue: "upper", test: (v) => /\p{Lu}/u.test(v) },
  { issue: "digit", test: (v) => /\d/u.test(v) }
];
function passwordIssues(password, email) {
  const issues = RULES.filter((rule) => !rule.test(password)).map((rule) => rule.issue);
  if (email && password.trim().toLowerCase() === email.trim().toLowerCase()) {
    issues.push("email");
  }
  return issues;
}
var PASSWORD_RULES_TEXT = "\u043D\u0435 \u043A\u043E\u0440\u043E\u0447\u0435 8 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432, \u0441\u043E \u0441\u0442\u0440\u043E\u0447\u043D\u043E\u0439 \u0438 \u0437\u0430\u0433\u043B\u0430\u0432\u043D\u043E\u0439 \u0431\u0443\u043A\u0432\u0430\u043C\u0438 \u0438 \u0445\u043E\u0442\u044F \u0431\u044B \u043E\u0434\u043D\u043E\u0439 \u0446\u0438\u0444\u0440\u043E\u0439";
var RU = {
  length: "\u041F\u0430\u0440\u043E\u043B\u044C \u043A\u043E\u0440\u043E\u0447\u0435 8 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432",
  lower: "\u0412 \u043F\u0430\u0440\u043E\u043B\u0435 \u043D\u0435 \u0445\u0432\u0430\u0442\u0430\u0435\u0442 \u0441\u0442\u0440\u043E\u0447\u043D\u043E\u0439 \u0431\u0443\u043A\u0432\u044B",
  upper: "\u0412 \u043F\u0430\u0440\u043E\u043B\u0435 \u043D\u0435 \u0445\u0432\u0430\u0442\u0430\u0435\u0442 \u0437\u0430\u0433\u043B\u0430\u0432\u043D\u043E\u0439 \u0431\u0443\u043A\u0432\u044B",
  digit: "\u0412 \u043F\u0430\u0440\u043E\u043B\u0435 \u043D\u0435 \u0445\u0432\u0430\u0442\u0430\u0435\u0442 \u0446\u0438\u0444\u0440\u044B",
  email: "\u041F\u0430\u0440\u043E\u043B\u044C \u043D\u0435 \u0434\u043E\u043B\u0436\u0435\u043D \u0441\u043E\u0432\u043F\u0430\u0434\u0430\u0442\u044C \u0441 \u0430\u0434\u0440\u0435\u0441\u043E\u043C \u043F\u043E\u0447\u0442\u044B"
};
function passwordProblem(password, email) {
  const issues = passwordIssues(password, email);
  if (issues.length === 0) return null;
  if (issues.length === 1 && issues[0]) return RU[issues[0]];
  if (issues.includes("email")) return RU.email;
  return `\u041F\u0430\u0440\u043E\u043B\u044C \u0434\u043E\u043B\u0436\u0435\u043D \u0431\u044B\u0442\u044C ${PASSWORD_RULES_TEXT}`;
}

// src/mail/letter-layout.ts
function bilingualLetter(input) {
  return {
    subject: input.subject,
    text: renderText(input),
    html: renderHtml(input)
  };
}
function renderText(input) {
  const section = (s) => [
    s.title,
    "",
    ...s.paragraphs.flatMap((p) => [p, ""]),
    ...s.facts?.length ? [...s.facts.map(([k, v]) => `${k}: ${v}`), ""] : [],
    ...s.afterFacts?.length ? [...s.afterFacts.flatMap((p) => [p, ""])] : [],
    ...s.cta ? [`${s.cta.label}:`, s.cta.url, ""] : [],
    ...s.note ? [s.note, ""] : []
  ];
  return [
    ...section(input.ru),
    "\u2014 \u2014 \u2014",
    "",
    ...section(input.en),
    input.footer.ru,
    input.footer.en
  ].join("\n");
}
function renderHtml(input) {
  const brand = input.brand ?? "ARPM";
  return `<!DOCTYPE html>
<html lang="ru">
<head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><title>${escapeHtml(input.subject)}</title></head>
<body style="margin:0; padding:0; background-color:#f6f5f2;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f6f5f2; padding:32px 12px;">
    <tr><td align="center">
      <table role="presentation" width="560" cellpadding="0" cellspacing="0" border="0" style="width:100%; max-width:560px; table-layout:fixed;">
        <tr><td align="center" style="padding:0 0 20px;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
            <td style="background-color:#3b4cbd; border-radius:10px; padding:8px 14px; font-family:Arial,Helvetica,sans-serif; font-size:15px; font-weight:bold; color:#ffffff;">${escapeHtml(brand)}</td>
          </tr></table>
        </td></tr>
        <tr><td style="background-color:#ffffff; border:1px solid #eceae5; border-radius:14px; padding:28px 28px 24px; font-family:Arial,Helvetica,sans-serif; font-size:15px; line-height:1.55; color:#1a1d24; word-break:break-word; overflow-wrap:anywhere;">
${renderSection(input.ru)}
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
            <td style="border-top:1px solid #eceae5; font-size:0; line-height:0; padding:22px 0 0;">&nbsp;</td>
          </tr></table>
${renderSection(input.en)}
        </td></tr>
        <tr><td style="padding:18px 8px 0; font-family:Arial,Helvetica,sans-serif; font-size:12px; line-height:1.5; color:#6b6e76;">
          ${escapeHtml(input.footer.ru)}<br />${escapeHtml(input.footer.en)}
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}
function renderSection(s) {
  const parts = [
    `          <p style="margin:0 0 10px; font-size:18px; font-weight:bold; line-height:1.3;">${escapeHtml(s.title)}</p>`,
    ...s.paragraphs.map((p) => `          <p style="margin:0 0 12px;">${escapeHtml(p)}</p>`)
  ];
  if (s.facts?.length) {
    const rows = s.facts.map(
      ([k, v]) => (
        // `white-space: nowrap` обязателен: у карточки письма стоит
        // `overflow-wrap: anywhere` ради длинных ссылок, и без него короткие
        // подписи рвутся по буквам — «Меропр иятие».
        `<tr><td style="padding:3px 12px 3px 0; color:#6b6e76; font-size:14px; vertical-align:top; white-space:nowrap;">${escapeHtml(k)}</td><td style="padding:3px 0; font-size:14px;">${escapeHtml(v)}</td></tr>`
      )
    ).join("");
    parts.push(
      `          <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 16px;">${rows}</table>`
    );
  }
  if (s.afterFacts?.length) {
    parts.push(
      ...s.afterFacts.map((p) => `          <p style="margin:0 0 12px;">${escapeHtml(p)}</p>`)
    );
  }
  if (s.cta) {
    parts.push(
      `          <p style="margin:16px 0 8px;"><a href="${escapeAttr(s.cta.url)}" style="display:inline-block; background-color:#3b4cbd; color:#ffffff; text-decoration:none; font-weight:bold; font-size:15px; padding:12px 24px; border-radius:10px;">${escapeHtml(s.cta.label)}</a></p>`
    );
  }
  if (s.note) {
    parts.push(
      `          <p style="margin:0; font-size:13px; color:#6b6e76;">${escapeHtml(s.note)}</p>`
    );
  }
  return parts.join("\n");
}
function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

// src/mail/event-letters.ts
var FOOTER = {
  ru: "\u041F\u0438\u0441\u044C\u043C\u043E \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u043E \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438, \u043E\u0442\u0432\u0435\u0447\u0430\u0442\u044C \u043D\u0430 \u043D\u0435\u0433\u043E \u043D\u0435 \u043D\u0443\u0436\u043D\u043E.",
  en: "This message was sent automatically. Please do not reply."
};
function factsRu(f) {
  return [
    ["\u041C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0435", f.title],
    ["\u0414\u0430\u0442\u044B", f.dates],
    ...f.place ? [["\u041C\u0435\u0441\u0442\u043E", f.place]] : [],
    ["\u0420\u043E\u043B\u044C", f.roleTitle]
  ];
}
function factsEn(f) {
  return [
    ["Event", f.title],
    ["Dates", f.dates],
    ...f.place ? [["Venue", f.place]] : [],
    ["Role", f.roleTitle]
  ];
}
var CABINET = { ru: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0432 \u043B\u0438\u0447\u043D\u043E\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435", en: "Open personal account" };
function participantDecisionLetter(input) {
  const who = input.firstName ? `${input.firstName}, ` : "";
  const whoEn = input.firstName ? `${input.firstName}, ` : "";
  const subject = input.kind === "approved" ? `\u0417\u0430\u044F\u0432\u043A\u0430 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0430: ${input.eventShortTitle} \xB7 Application approved` : input.kind === "rejected" ? `\u0417\u0430\u044F\u0432\u043A\u0430 \u043E\u0442\u043A\u043B\u043E\u043D\u0435\u043D\u0430: ${input.eventShortTitle} \xB7 Application declined` : `\u0412\u044B \u0432 \u043B\u0438\u0441\u0442\u0435 \u043E\u0436\u0438\u0434\u0430\u043D\u0438\u044F: ${input.eventShortTitle} \xB7 You are on the waiting list`;
  const ruTitle = input.kind === "approved" ? `${who}\u0432\u0430\u0448\u0430 \u0437\u0430\u044F\u0432\u043A\u0430 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0430` : input.kind === "rejected" ? `${who}\u0437\u0430\u044F\u0432\u043A\u0443 \u043E\u0442\u043A\u043B\u043E\u043D\u0438\u043B\u0438` : `${who}\u0432\u044B \u0432 \u043B\u0438\u0441\u0442\u0435 \u043E\u0436\u0438\u0434\u0430\u043D\u0438\u044F`;
  const enTitle = input.kind === "approved" ? `${whoEn}your application is approved` : input.kind === "rejected" ? `${whoEn}your application was declined` : `${whoEn}you are on the waiting list`;
  const ruBody = input.kind === "approved" ? ["\u041C\u0435\u0441\u0442\u043E \u0437\u0430 \u0432\u0430\u043C\u0438. \u041F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u0430, \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0430 \u0438 \u0432\u0430\u0448\u0438 \u0434\u0430\u043D\u043D\u044B\u0435 \u2014 \u0432 \u043B\u0438\u0447\u043D\u043E\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435."] : input.kind === "rejected" ? [
    "\u041A \u0441\u043E\u0436\u0430\u043B\u0435\u043D\u0438\u044E, \u043E\u0440\u0433\u043A\u043E\u043C\u0438\u0442\u0435\u0442 \u043D\u0435 \u0441\u043C\u043E\u0433 \u043F\u0440\u0438\u043D\u044F\u0442\u044C \u0432\u0430\u0448\u0443 \u0437\u0430\u044F\u0432\u043A\u0443.",
    ...input.reason ? [`\u041F\u0440\u0438\u0447\u0438\u043D\u0430: ${input.reason}`] : []
  ] : [
    "\u041C\u0435\u0441\u0442 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442. \u0415\u0441\u043B\u0438 \u043E\u043D\u0438 \u043F\u043E\u044F\u0432\u044F\u0442\u0441\u044F, \u043E\u0440\u0433\u043A\u043E\u043C\u0438\u0442\u0435\u0442 \u0432\u0435\u0440\u043D\u0451\u0442\u0441\u044F \u043A \u0432\u0430\u0448\u0435\u0439 \u0437\u0430\u044F\u0432\u043A\u0435 \u2014 \u0441\u043B\u0435\u0434\u0438\u0442\u044C \u0437\u0430 \u0441\u0442\u0430\u0442\u0443\u0441\u043E\u043C \u043C\u043E\u0436\u043D\u043E \u0432 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435."
  ];
  const enBody = input.kind === "approved" ? [
    "Your place is confirmed. The programme, the venue and your details are in your personal account."
  ] : input.kind === "rejected" ? [
    "Unfortunately, the organising committee could not accept your application.",
    // Причину не переводим: это слова оргкомитета.
    ...input.reason ? [`Reason (in Russian): ${input.reason}`] : []
  ] : [
    "There are no places at the moment. If any appear, the organising committee will return to your application \u2014 you can follow the status in your account."
  ];
  return bilingualLetter({
    subject,
    ru: {
      title: ruTitle,
      paragraphs: ruBody,
      facts: factsRu(input.facts),
      cta: { label: CABINET.ru, url: input.cabinetUrl }
    },
    en: {
      title: enTitle,
      paragraphs: enBody,
      facts: factsEn(input.facts),
      cta: { label: CABINET.en, url: input.cabinetUrl }
    },
    footer: FOOTER
  });
}
function intakeInviteLetter(input) {
  const byCompany = input.companyName ? `\u0412\u0430\u0441 \u0437\u0430\u043F\u0438\u0441\u0430\u043B\u0430 \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F \xAB${input.companyName}\xBB` : "\u0412\u0430\u0441 \u0437\u0430\u043F\u0438\u0441\u0430\u043B\u0438";
  const byCompanyEn = input.companyName ? `You were registered by ${input.companyName}` : "You have been registered";
  const role = input.roleTitle ? ` \u0432 \u0440\u043E\u043B\u0438 \xAB${input.roleTitle}\xBB` : "";
  const roleEn = input.roleTitle ? ` as \xAB${input.roleTitle}\xBB` : "";
  return bilingualLetter({
    subject: `\u0417\u0430\u0432\u0435\u0440\u0448\u0438\u0442\u0435 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044E: ${input.eventShortTitle} \xB7 Complete your registration`,
    ru: {
      title: `\u0412\u0430\u0441 \u043F\u0440\u0438\u0433\u043B\u0430\u0441\u0438\u043B\u0438 \u043D\u0430 \xAB${input.eventTitle}\xBB`,
      paragraphs: [
        `${byCompany}${role}. \u0427\u0442\u043E\u0431\u044B \u0437\u0430\u0432\u0435\u0440\u0448\u0438\u0442\u044C \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044E, \u0437\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u0430\u043D\u043A\u0435\u0442\u0443 \u0438 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u0435 \u0441\u043E\u0433\u043B\u0430\u0441\u0438\u0435 \u043D\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0443 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445.`
      ],
      cta: { label: "\u0417\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u044C \u0430\u043D\u043A\u0435\u0442\u0443", url: input.formUrl },
      note: "\u041F\u043E\u043A\u0430 \u0430\u043D\u043A\u0435\u0442\u0430 \u043D\u0435 \u0437\u0430\u043F\u043E\u043B\u043D\u0435\u043D\u0430, \u0437\u0430\u044F\u0432\u043A\u0430 \u043D\u0435 \u0440\u0430\u0441\u0441\u043C\u0430\u0442\u0440\u0438\u0432\u0430\u0435\u0442\u0441\u044F."
    },
    en: {
      title: `You have been invited to \xAB${input.eventTitle}\xBB`,
      paragraphs: [
        `${byCompanyEn}${roleEn}. Fill in the form and confirm your consent to personal data processing to complete the registration.`
      ],
      cta: { label: "Fill in the form", url: input.formUrl },
      note: "Until the form is completed, the application is not reviewed."
    },
    footer: FOOTER
  });
}
function announcementLetter(input) {
  const from = `\u0421\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0435 \u043E\u0442 \u043E\u0440\u0433\u043A\u043E\u043C\u0438\u0442\u0435\u0442\u0430 \xAB${input.eventTitle}\xBB`;
  const fromEn = `A message from the organising committee of \xAB${input.eventTitle}\xBB`;
  return bilingualLetter({
    subject: `${input.title} \xB7 ${input.eventTitle}`,
    ru: {
      title: input.title,
      paragraphs: [from, input.body],
      cta: { label: CABINET.ru, url: input.cabinetUrl }
    },
    en: {
      title: input.title,
      // Текст анонса не дублируем: он один и тот же, а второй раз тем же
      // языком читается как сбой письма.
      paragraphs: [fromEn],
      cta: { label: CABINET.en, url: input.cabinetUrl }
    },
    footer: FOOTER
  });
}
function birthDateRequestLetter(input) {
  return bilingualLetter({
    subject: `\u0417\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u043D\u0435\u0434\u043E\u0441\u0442\u0430\u044E\u0449\u0443\u044E \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044E: ${input.eventShortTitle} \xB7 Please complete your registration details`,
    ru: {
      title: "\u0417\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u043D\u0435\u0434\u043E\u0441\u0442\u0430\u044E\u0449\u0443\u044E \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044E",
      paragraphs: [
        // Название — как есть из карточки мероприятия, после «на»: оно там в
        // именительном падеже («Финал …»), который с «на» и совпадает.
        `\u041F\u0440\u043E\u0441\u0438\u043C \u0432\u0430\u0441 \u043F\u0435\u0440\u0435\u0439\u0442\u0438 \u0432 \u043B\u0438\u0447\u043D\u044B\u0439 \u043A\u0430\u0431\u0438\u043D\u0435\u0442 arpm.pro \u0438 \u0437\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u044C \u043D\u0435\u0434\u043E\u0441\u0442\u0430\u044E\u0449\u0443\u044E \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044E \u0434\u043B\u044F \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u0438 \u043D\u0430 ${input.eventTitle}.`
      ],
      cta: { label: "\u041F\u0435\u0440\u0435\u0439\u0442\u0438 \u0432 \u043B\u0438\u0447\u043D\u044B\u0439 \u043A\u0430\u0431\u0438\u043D\u0435\u0442", url: input.cabinetUrl },
      note: "\u041D\u0435\u0442 \u043F\u0430\u0440\u043E\u043B\u044F \u2014 \u043D\u0430\u0436\u043C\u0438\u0442\u0435 \xAB\u0417\u0430\u0431\u044B\u043B\u0438 \u043F\u0430\u0440\u043E\u043B\u044C?\xBB \u043D\u0430 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0435 \u0432\u0445\u043E\u0434\u0430."
    },
    en: {
      title: "Please complete your registration details",
      paragraphs: [
        `Please go to your arpm.pro personal account and fill in the missing information for your registration for ${input.eventTitle}.`
      ],
      cta: { label: "Go to personal account", url: input.cabinetUrl },
      note: "No password? Click \xABForgot password?\xBB on the sign-in page."
    },
    footer: FOOTER
  });
}
function staffInviteLetter(input) {
  return bilingualLetter({
    subject: `\u0412\u0430\u0441 \u0434\u043E\u0431\u0430\u0432\u0438\u043B\u0438 \u0432 \u043E\u0440\u0433\u043A\u043E\u043C\u0430\u043D\u0434\u0443: ${input.eventTitle} \xB7 You have been added to the team`,
    ru: {
      title: `\u0412\u044B \u0432 \u043E\u0440\u0433\u043A\u043E\u043C\u0430\u043D\u0434\u0435 \xAB${input.eventTitle}\xBB`,
      paragraphs: [`\u0420\u043E\u043B\u044C \u2014 \xAB${input.roleLabel}\xBB. ${input.roleHint}`],
      cta: { label: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043B\u0438\u0447\u043D\u044B\u0439 \u043A\u0430\u0431\u0438\u043D\u0435\u0442", url: input.cabinetUrl },
      note: `\u0421\u0440\u0430\u0437\u0443 \u043A \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u044E: ${input.eventUrl}
\u0412\u0445\u043E\u0434 \u2014 \u043F\u043E \u0432\u0430\u0448\u0435\u0439 \u0443\u0447\u0451\u0442\u043D\u043E\u0439 \u0437\u0430\u043F\u0438\u0441\u0438 ARPM.`
    },
    en: {
      title: `You joined the team of \xAB${input.eventTitle}\xBB`,
      paragraphs: [`Your role is \xAB${input.roleLabelEn}\xBB. ${input.roleHintEn}`],
      cta: { label: "Open personal account", url: input.cabinetUrl },
      note: `Straight to the event: ${input.eventUrl}
Sign in with your ARPM account.`
    },
    footer: FOOTER
  });
}
function platformAdminLetter(input) {
  return bilingualLetter({
    subject: "\u0412\u0430\u043C \u0432\u044B\u0434\u0430\u043D\u044B \u043F\u0440\u0430\u0432\u0430 \u0430\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440\u0430 \u043F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u044B \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0439 \xB7 Platform administrator rights granted",
    ru: {
      title: "\u0412\u044B \u0430\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440 \u043F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u044B \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0439",
      paragraphs: [
        "\u041F\u0440\u0430\u0432\u0430 \u0434\u0430\u044E\u0442 \u0434\u043E\u0441\u0442\u0443\u043F \u043A\u043E \u0432\u0441\u0435\u043C \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u044F\u043C \u0438 \u043A \u0437\u0430\u044F\u0432\u043A\u0430\u043C \u0441 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u043C\u0438 \u0434\u0430\u043D\u043D\u044B\u043C\u0438 \u0443\u0447\u0430\u0441\u0442\u043D\u0438\u043A\u043E\u0432 \u2014 \u043A\u043E \u0432\u0441\u0435\u043C, \u0430 \u043D\u0435 \u0442\u043E\u043B\u044C\u043A\u043E \u043A \u0441\u0432\u043E\u0438\u043C. \u041F\u043E\u043B\u044C\u0437\u0443\u0439\u0442\u0435\u0441\u044C \u0438\u043C\u0438 \u0431\u0435\u0440\u0435\u0436\u043D\u043E."
      ],
      cta: { label: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043B\u0438\u0447\u043D\u044B\u0439 \u043A\u0430\u0431\u0438\u043D\u0435\u0442", url: input.cabinetUrl },
      note: `\u0421\u0440\u0430\u0437\u0443 \u043A \u043F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0435: ${input.platformUrl}`
    },
    en: {
      title: "You are a platform administrator",
      paragraphs: [
        "These rights give access to every event and to applications containing participants' personal data \u2014 to all of them, not only your own. Please use them carefully."
      ],
      cta: { label: "Open personal account", url: input.cabinetUrl },
      note: `Straight to the platform: ${input.platformUrl}`
    },
    footer: FOOTER
  });
}

// src/mail/password-reset-letter.ts
function passwordResetLetter(input) {
  const who = input.firstName ? `${input.firstName}, ` : "";
  const ttl = String(input.ttlMinutes);
  return bilingualLetter({
    brand: "ARPM ID",
    subject: "\u0423\u0441\u0442\u0430\u043D\u043E\u0432\u043A\u0430 \u043F\u0430\u0440\u043E\u043B\u044F \u0432 ARPM ID \xB7 Set your ARPM ID password",
    ru: {
      title: `${who}\u0437\u0430\u0434\u0430\u0439\u0442\u0435 \u043F\u0430\u0440\u043E\u043B\u044C`,
      paragraphs: [
        "\u041A\u0442\u043E-\u0442\u043E \u0437\u0430\u043F\u0440\u043E\u0441\u0438\u043B \u0443\u0441\u0442\u0430\u043D\u043E\u0432\u043A\u0443 \u043D\u043E\u0432\u043E\u0433\u043E \u043F\u0430\u0440\u043E\u043B\u044F \u0434\u043B\u044F \u044D\u0442\u043E\u0433\u043E \u0430\u0434\u0440\u0435\u0441\u0430. \u0415\u0441\u043B\u0438 \u044D\u0442\u043E \u0431\u044B\u043B\u0438 \u0432\u044B \u2014 \u0437\u0430\u0434\u0430\u0439\u0442\u0435 \u043F\u0430\u0440\u043E\u043B\u044C \u043F\u043E \u043A\u043D\u043E\u043F\u043A\u0435. \u0415\u0441\u043B\u0438 \u043D\u0435\u0442, \u043D\u0438\u0447\u0435\u0433\u043E \u0434\u0435\u043B\u0430\u0442\u044C \u043D\u0435 \u043D\u0443\u0436\u043D\u043E: \u043F\u0440\u0435\u0436\u043D\u0438\u0439 \u043F\u0430\u0440\u043E\u043B\u044C \u043F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442 \u0440\u0430\u0431\u043E\u0442\u0430\u0442\u044C."
      ],
      cta: { label: "\u0417\u0430\u0434\u0430\u0442\u044C \u043F\u0430\u0440\u043E\u043B\u044C", url: input.link },
      note: `\u0421\u0441\u044B\u043B\u043A\u0430 \u0434\u0435\u0439\u0441\u0442\u0432\u0443\u0435\u0442 ${ttl} \u043C\u0438\u043D. \u0438 \u043E\u0442\u043A\u0440\u044B\u0432\u0430\u0435\u0442\u0441\u044F \u043E\u0434\u0438\u043D \u0440\u0430\u0437.`
    },
    en: {
      title: `${who}set your password`,
      paragraphs: [
        "Someone requested a new password for this address. If that was you, set it using the button. If not, no action is needed \u2014 your current password keeps working."
      ],
      cta: { label: "Set password", url: input.link },
      note: `The link is valid for ${ttl} min and opens once.`
    },
    footer: {
      ru: "\u0415\u0441\u043B\u0438 \u0432\u044B \u043D\u0435 \u0437\u0430\u043F\u0440\u0430\u0448\u0438\u0432\u0430\u043B\u0438 \u0441\u043C\u0435\u043D\u0443 \u043F\u0430\u0440\u043E\u043B\u044F, \u043F\u0438\u0441\u044C\u043C\u043E \u043C\u043E\u0436\u043D\u043E \u043F\u0440\u043E\u0441\u0442\u043E \u0443\u0434\u0430\u043B\u0438\u0442\u044C.",
      en: "If you did not request this, simply delete the message."
    }
  });
}

// src/mail/registration-receipt-letter.ts
function registrationReceiptLetter(input) {
  const who = input.firstName ? `${input.firstName}, ` : "";
  const subject = input.kind === "moderated" ? `\u0417\u0430\u044F\u0432\u043A\u0430 \u043F\u043E\u043B\u0443\u0447\u0435\u043D\u0430: ${input.eventShortTitle} \xB7 Application received` : input.kind === "approved" ? `\u0412\u044B \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D\u044B: ${input.eventShortTitle} \xB7 You are registered` : `\u0412\u0430\u0441 \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043B\u0438: ${input.eventShortTitle} \xB7 You have been registered`;
  const titleRu = input.kind === "moderated" ? `${who}\u043C\u044B \u043F\u043E\u043B\u0443\u0447\u0438\u043B\u0438 \u0432\u0430\u0448\u0443 \u0437\u0430\u044F\u0432\u043A\u0443` : input.kind === "approved" ? `${who}\u0432\u044B \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D\u044B` : `${who}\u0432\u0430\u0441 \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043B\u0430 \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F`;
  const titleEn = input.kind === "moderated" ? `${who}we have received your application` : input.kind === "approved" ? `${who}you are registered` : `${who}you were registered by your organisation`;
  const bodyRu = input.kind === "moderated" ? ["\u0417\u0430\u044F\u0432\u043A\u0443 \u0440\u0430\u0441\u0441\u043C\u0430\u0442\u0440\u0438\u0432\u0430\u0435\u0442 \u043E\u0440\u0433\u043A\u043E\u043C\u0438\u0442\u0435\u0442. \u0420\u0435\u0448\u0435\u043D\u0438\u0435 \u043F\u0440\u0438\u0434\u0451\u0442 \u043D\u0430 \u044D\u0442\u043E\u0442 \u0430\u0434\u0440\u0435\u0441 \u0438 \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u0432 \u043B\u0438\u0447\u043D\u043E\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435."] : input.kind === "approved" ? [
    "\u041C\u0435\u0441\u0442\u043E \u0437\u0430 \u0432\u0430\u043C\u0438. \u041F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u0430, \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0430 \u0438 \u0432\u0430\u0448\u0438 \u0434\u0430\u043D\u043D\u044B\u0435 \u2014 \u0432 \u043B\u0438\u0447\u043D\u043E\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435. \u0422\u0430\u043C \u0436\u0435 \u043C\u043E\u0436\u043D\u043E \u043E\u0442\u043A\u0430\u0437\u0430\u0442\u044C\u0441\u044F \u043E\u0442 \u0443\u0447\u0430\u0441\u0442\u0438\u044F, \u0435\u0441\u043B\u0438 \u043F\u043B\u0430\u043D\u044B \u0438\u0437\u043C\u0435\u043D\u044F\u0442\u0441\u044F."
  ] : [
    `\u0412\u0430\u0441 \u0437\u0430\u043F\u0438\u0441\u0430\u043B\u0430${input.companyName ? ` \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F \xAB${input.companyName}\xBB` : " \u0432\u0430\u0448\u0430 \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F"}.`,
    "\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0441\u0432\u043E\u0438 \u0434\u0430\u043D\u043D\u044B\u0435 \u0432 \u043B\u0438\u0447\u043D\u043E\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435: \u0438\u043C\u044F, \u0434\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u044C \u0438 \u043A\u043E\u043D\u0442\u0430\u043A\u0442\u044B \u0437\u0430\u043F\u043E\u043B\u043D\u044F\u043B\u0430 \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F. \u0415\u0441\u043B\u0438 \u0447\u0442\u043E-\u0442\u043E \u043D\u0435 \u0442\u0430\u043A, \u043F\u043E\u043F\u0440\u0430\u0432\u044C\u0442\u0435 \u0442\u0430\u043C \u0436\u0435 \u0438\u043B\u0438 \u043D\u0430\u043F\u0438\u0448\u0438\u0442\u0435 \u0432 \u043E\u0440\u0433\u043A\u043E\u043C\u0438\u0442\u0435\u0442."
  ];
  const bodyEn = input.kind === "moderated" ? [
    "The organising committee is reviewing it. The decision will be sent to this address and will appear in your personal account."
  ] : input.kind === "approved" ? [
    "Your place is confirmed. The programme, the venue and your details are in your personal account, where you can also cancel your participation if your plans change."
  ] : [
    `You were registered by${input.companyName ? ` ${input.companyName}` : " your organisation"}.`,
    "Please check your details in your personal account: the name, position and contacts were filled in by the organisation. Correct them there or write to the organising committee."
  ];
  const letterRu = input.letter ? `\u041E\u0441\u0442\u0430\u0451\u0442\u0441\u044F \u043F\u0440\u0438\u043B\u043E\u0436\u0438\u0442\u044C \u043F\u0438\u0441\u044C\u043C\u043E \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u0438: \u0441\u043A\u0430\u043D \u043D\u0430 \u0431\u043B\u0430\u043D\u043A\u0435 \u043E \u043D\u0430\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u0438 \u0440\u0430\u0431\u043E\u0442\u043D\u0438\u043A\u0430 \u2014 \u043D\u0430 ${input.letter.url}${input.letter.deadline ? `, \u0434\u043E ${input.letter.deadline}` : ""}. \u0415\u0441\u043B\u0438 \u043F\u0438\u0441\u044C\u043C\u043E \u0437\u0430 \u0432\u0430\u0441 \u043F\u043E\u0434\u0430\u0451\u0442 \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F, \u043E\u0442\u043C\u0435\u0442\u043A\u0430 \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u0441\u0430\u043C\u0430.` : null;
  const letterEn = input.letter ? `One thing is left: the organisation's letter of assignment. Upload a scan at ${input.letter.url}${input.letter.deadline ? `, before ${input.letter.deadline}` : ""}. If your organisation submits it for you, the mark will appear on its own.` : null;
  return bilingualLetter({
    subject,
    ru: {
      title: titleRu,
      paragraphs: bodyRu,
      ...letterRu ? { afterFacts: [letterRu] } : {},
      facts: [
        ["\u041C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0435", input.eventTitle],
        ["\u0414\u0430\u0442\u044B", input.dates],
        ...input.place ? [["\u041C\u0435\u0441\u0442\u043E", input.place]] : [],
        ["\u0420\u043E\u043B\u044C", input.roleTitle]
      ],
      cta: { label: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0432 \u043B\u0438\u0447\u043D\u043E\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435", url: input.cabinetUrl },
      ...input.contacts ? { note: `\u0412\u043E\u043F\u0440\u043E\u0441\u044B \u043F\u043E \u043C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u044E: ${input.contacts}` } : {}
    },
    en: {
      title: titleEn,
      paragraphs: bodyEn,
      ...letterEn ? { afterFacts: [letterEn] } : {},
      facts: [
        ["Event", input.eventTitle],
        ["Dates", input.dates],
        ...input.place ? [["Venue", input.place]] : [],
        ["Role", input.roleTitle]
      ],
      cta: { label: "Open personal account", url: input.cabinetUrl },
      ...input.contacts ? { note: `Questions about the event: ${input.contacts}` } : {}
    },
    footer: {
      ru: "\u041F\u0438\u0441\u044C\u043C\u043E \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u043E \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438, \u043E\u0442\u0432\u0435\u0447\u0430\u0442\u044C \u043D\u0430 \u043D\u0435\u0433\u043E \u043D\u0435 \u043D\u0443\u0436\u043D\u043E.",
      en: "This message was sent automatically. Please do not reply."
    }
  });
}

// src/constants/event-types.ts
var ACTIVITY_TYPES = [
  "event",
  "course",
  "diagnostic",
  "certification",
  "project"
];
var ACTIVITY_STATUSES = [
  "registered",
  "in_progress",
  "completed",
  "cancelled",
  "failed"
];
var ACTIVITY_TYPE_LABELS_RU = {
  event: "\u041C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u0435",
  course: "\u041A\u0443\u0440\u0441",
  diagnostic: "\u0414\u0438\u0430\u0433\u043D\u043E\u0441\u0442\u0438\u043A\u0430",
  certification: "\u0421\u0435\u0440\u0442\u0438\u0444\u0438\u043A\u0430\u0446\u0438\u044F",
  project: "\u041F\u0440\u043E\u0435\u043A\u0442"
};
var ACTIVITY_STATUS_LABELS_RU = {
  registered: "\u0417\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D",
  in_progress: "\u0412 \u043F\u0440\u043E\u0446\u0435\u0441\u0441\u0435",
  completed: "\u0417\u0430\u0432\u0435\u0440\u0448\u0435\u043D\u043E",
  cancelled: "\u041E\u0442\u043C\u0435\u043D\u0435\u043D\u043E",
  failed: "\u041D\u0435 \u043F\u0440\u043E\u0439\u0434\u0435\u043D\u043E"
};

// src/constants/event-catalog.ts
var EVENT_SOURCES = ["molfest", "inpdp", "events", "manual"];
var EVENT_LIFECYCLES = ["draft", "published", "cancelled"];
var REGISTRATION_STATUSES = [
  "registered",
  "confirmed",
  "attended",
  "cancelled",
  "waitlisted"
];
var EVENT_SOURCE_LABELS_RU = {
  molfest: "\u041C\u043E\u043B\u043E\u0434\u0451\u0436\u043D\u044B\u0439 \u0444\u0435\u0441\u0442\u0438\u0432\u0430\u043B\u044C",
  inpdp: "\u041C\u0435\u0436\u0434\u0443\u043D\u0430\u0440\u043E\u0434\u043D\u0430\u044F \u043F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0430",
  events: "\u041C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u044F ARPM",
  manual: "\u0412\u0440\u0443\u0447\u043D\u0443\u044E"
};
var REGISTRATION_STATUS_LABELS_RU = {
  registered: "\u0417\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D",
  confirmed: "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u043E",
  attended: "\u041F\u043E\u0441\u0435\u0449\u0435\u043D\u043E",
  cancelled: "\u041E\u0442\u043C\u0435\u043D\u0435\u043D\u043E",
  waitlisted: "\u041B\u0438\u0441\u0442 \u043E\u0436\u0438\u0434\u0430\u043D\u0438\u044F"
};

// src/constants/intranet-modules.ts
var INTRANET_MODERATABLE_MODULES = [
  { key: "documents", order: 10 },
  { key: "emergency", order: 20 },
  { key: "onboarding", order: 30 },
  { key: "vacations", order: 40 },
  { key: "projects", order: 50 },
  { key: "assignments", order: 60 }
];
var INTRANET_MODULE_KEYS = INTRANET_MODERATABLE_MODULES.map(
  (m) => m.key
);
var INTRANET_MODULE_LABELS_RU = {
  documents: "\u0414\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u044B",
  emergency: "\u041F\u0435\u0440\u0432\u0430\u044F \u043F\u043E\u043C\u043E\u0449\u044C / \u0427\u0421",
  onboarding: "\u041F\u0430\u043F\u043A\u0430 \u043D\u043E\u0432\u0438\u0447\u043A\u0430",
  vacations: "\u041E\u0442\u043F\u0443\u0441\u043A\u0430",
  projects: "\u041F\u0440\u043E\u0435\u043A\u0442\u044B",
  assignments: "\u041F\u043E\u0440\u0443\u0447\u0435\u043D\u0438\u044F"
};

// src/constants/project-colors.ts
var PROJECT_COLORS = [
  "green",
  "pink",
  "yellow",
  "blue",
  "violet",
  "orange",
  "teal",
  "gray",
  "red",
  "lime",
  "cyan",
  "indigo",
  "purple",
  "rose",
  "amber",
  "brown"
];
var HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
function isPersonalProjectColor(v) {
  return typeof v === "string" && (PROJECT_COLORS.includes(v) || HEX_COLOR_RE.test(v));
}

// src/constants/content.ts
var CONTENT_KINDS = ["promo", "story"];
var CONTENT_KIND_LABELS_RU = {
  promo: "\u041F\u0440\u043E\u043C\u043E",
  story: "\u0421\u0442\u043E\u0440\u0438\u0441"
};

// src/constants/services.ts
var SERVICE_CATEGORIES = [
  "general",
  "events",
  "analytics",
  "admin",
  "integration"
];
var SERVICE_CATEGORY_LABELS_RU = {
  general: "\u0411\u0430\u0437\u043E\u0432\u044B\u0435",
  events: "\u041C\u0435\u0440\u043E\u043F\u0440\u0438\u044F\u0442\u0438\u044F",
  analytics: "\u0410\u043D\u0430\u043B\u0438\u0442\u0438\u043A\u0430",
  admin: "\u0410\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u0435",
  integration: "\u0418\u043D\u0442\u0435\u0433\u0440\u0430\u0446\u0438\u0438"
};
var SERVICE_VISIBILITIES = ["public", "restricted", "admin_only"];
var SERVICE_VISIBILITY_LABELS_RU = {
  public: "\u0412\u0438\u0434\u0435\u043D \u0432\u0441\u0435\u043C",
  restricted: "\u041F\u043E \u0440\u043E\u043B\u044F\u043C \u0441\u0435\u0440\u0432\u0438\u0441\u0430",
  admin_only: "\u0422\u043E\u043B\u044C\u043A\u043E \u0430\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440\u0430\u043C"
};

// src/constants/roles.ts
var LK_ADMIN_ROLE_CODE = "lk-admin";
var LK_ADMIN_ROLE_REF = { serviceCode: null, code: LK_ADMIN_ROLE_CODE };
function formatRoleRef(ref) {
  return ref.serviceCode ? `${ref.serviceCode}:${ref.code}` : ref.code;
}
function parseRoleRef(input) {
  const colon = input.indexOf(":");
  if (colon === -1) {
    return { serviceCode: null, code: input };
  }
  return {
    serviceCode: input.slice(0, colon),
    code: input.slice(colon + 1)
  };
}
var ROLE_LK_ADMIN = "lk-arpm:admin";
var ROLE_LK_USER = "lk-arpm:user";
function roleName(client, role) {
  return `${client}:${role}`;
}
function parseRoleName(input) {
  const match = /^([a-z0-9-]+):([a-z0-9-_]+)$/.exec(input);
  if (!match) {
    return null;
  }
  return { client: match[1], role: match[2] };
}

// src/constants/regions.ts
var RU_REGIONS = Object.freeze({
  "01": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0410\u0434\u044B\u0433\u0435\u044F",
  "02": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0411\u0430\u0448\u043A\u043E\u0440\u0442\u043E\u0441\u0442\u0430\u043D",
  "03": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0411\u0443\u0440\u044F\u0442\u0438\u044F",
  "04": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0410\u043B\u0442\u0430\u0439",
  "05": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0414\u0430\u0433\u0435\u0441\u0442\u0430\u043D",
  "06": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0418\u043D\u0433\u0443\u0448\u0435\u0442\u0438\u044F",
  "07": "\u041A\u0430\u0431\u0430\u0440\u0434\u0438\u043D\u043E-\u0411\u0430\u043B\u043A\u0430\u0440\u0441\u043A\u0430\u044F \u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430",
  "08": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u041A\u0430\u043B\u043C\u044B\u043A\u0438\u044F",
  "09": "\u041A\u0430\u0440\u0430\u0447\u0430\u0435\u0432\u043E-\u0427\u0435\u0440\u043A\u0435\u0441\u0441\u043A\u0430\u044F \u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430",
  "10": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u041A\u0430\u0440\u0435\u043B\u0438\u044F",
  "11": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u041A\u043E\u043C\u0438",
  "12": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u041C\u0430\u0440\u0438\u0439 \u042D\u043B",
  "13": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u041C\u043E\u0440\u0434\u043E\u0432\u0438\u044F",
  "14": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0421\u0430\u0445\u0430 (\u042F\u043A\u0443\u0442\u0438\u044F)",
  "15": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0421\u0435\u0432\u0435\u0440\u043D\u0430\u044F \u041E\u0441\u0435\u0442\u0438\u044F \u2014 \u0410\u043B\u0430\u043D\u0438\u044F",
  "16": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0422\u0430\u0442\u0430\u0440\u0441\u0442\u0430\u043D",
  "17": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0422\u044B\u0432\u0430",
  "18": "\u0423\u0434\u043C\u0443\u0440\u0442\u0441\u043A\u0430\u044F \u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430",
  "19": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u0425\u0430\u043A\u0430\u0441\u0438\u044F",
  "20": "\u0427\u0435\u0447\u0435\u043D\u0441\u043A\u0430\u044F \u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430",
  "21": "\u0427\u0443\u0432\u0430\u0448\u0441\u043A\u0430\u044F \u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430",
  "22": "\u0410\u043B\u0442\u0430\u0439\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "23": "\u041A\u0440\u0430\u0441\u043D\u043E\u0434\u0430\u0440\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "24": "\u041A\u0440\u0430\u0441\u043D\u043E\u044F\u0440\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "25": "\u041F\u0440\u0438\u043C\u043E\u0440\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "26": "\u0421\u0442\u0430\u0432\u0440\u043E\u043F\u043E\u043B\u044C\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "27": "\u0425\u0430\u0431\u0430\u0440\u043E\u0432\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "28": "\u0410\u043C\u0443\u0440\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "29": "\u0410\u0440\u0445\u0430\u043D\u0433\u0435\u043B\u044C\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "30": "\u0410\u0441\u0442\u0440\u0430\u0445\u0430\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "31": "\u0411\u0435\u043B\u0433\u043E\u0440\u043E\u0434\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "32": "\u0411\u0440\u044F\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "33": "\u0412\u043B\u0430\u0434\u0438\u043C\u0438\u0440\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "34": "\u0412\u043E\u043B\u0433\u043E\u0433\u0440\u0430\u0434\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "35": "\u0412\u043E\u043B\u043E\u0433\u043E\u0434\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "36": "\u0412\u043E\u0440\u043E\u043D\u0435\u0436\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "37": "\u0418\u0432\u0430\u043D\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "38": "\u0418\u0440\u043A\u0443\u0442\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "39": "\u041A\u0430\u043B\u0438\u043D\u0438\u043D\u0433\u0440\u0430\u0434\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "40": "\u041A\u0430\u043B\u0443\u0436\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "41": "\u041A\u0430\u043C\u0447\u0430\u0442\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "42": "\u041A\u0435\u043C\u0435\u0440\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C \u2014 \u041A\u0443\u0437\u0431\u0430\u0441\u0441",
  "43": "\u041A\u0438\u0440\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "44": "\u041A\u043E\u0441\u0442\u0440\u043E\u043C\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "45": "\u041A\u0443\u0440\u0433\u0430\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "46": "\u041A\u0443\u0440\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "47": "\u041B\u0435\u043D\u0438\u043D\u0433\u0440\u0430\u0434\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "48": "\u041B\u0438\u043F\u0435\u0446\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "49": "\u041C\u0430\u0433\u0430\u0434\u0430\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "50": "\u041C\u043E\u0441\u043A\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "51": "\u041C\u0443\u0440\u043C\u0430\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "52": "\u041D\u0438\u0436\u0435\u0433\u043E\u0440\u043E\u0434\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "53": "\u041D\u043E\u0432\u0433\u043E\u0440\u043E\u0434\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "54": "\u041D\u043E\u0432\u043E\u0441\u0438\u0431\u0438\u0440\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "55": "\u041E\u043C\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "56": "\u041E\u0440\u0435\u043D\u0431\u0443\u0440\u0433\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "57": "\u041E\u0440\u043B\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "58": "\u041F\u0435\u043D\u0437\u0435\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "59": "\u041F\u0435\u0440\u043C\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "60": "\u041F\u0441\u043A\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "61": "\u0420\u043E\u0441\u0442\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "62": "\u0420\u044F\u0437\u0430\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "63": "\u0421\u0430\u043C\u0430\u0440\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "64": "\u0421\u0430\u0440\u0430\u0442\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "65": "\u0421\u0430\u0445\u0430\u043B\u0438\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "66": "\u0421\u0432\u0435\u0440\u0434\u043B\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "67": "\u0421\u043C\u043E\u043B\u0435\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "68": "\u0422\u0430\u043C\u0431\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "69": "\u0422\u0432\u0435\u0440\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "70": "\u0422\u043E\u043C\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "71": "\u0422\u0443\u043B\u044C\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "72": "\u0422\u044E\u043C\u0435\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "73": "\u0423\u043B\u044C\u044F\u043D\u043E\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "74": "\u0427\u0435\u043B\u044F\u0431\u0438\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "75": "\u0417\u0430\u0431\u0430\u0439\u043A\u0430\u043B\u044C\u0441\u043A\u0438\u0439 \u043A\u0440\u0430\u0439",
  "76": "\u042F\u0440\u043E\u0441\u043B\u0430\u0432\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "77": "\u041C\u043E\u0441\u043A\u0432\u0430",
  "78": "\u0421\u0430\u043D\u043A\u0442-\u041F\u0435\u0442\u0435\u0440\u0431\u0443\u0440\u0433",
  "79": "\u0415\u0432\u0440\u0435\u0439\u0441\u043A\u0430\u044F \u0430\u0432\u0442\u043E\u043D\u043E\u043C\u043D\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "80": "\u041D\u0435\u043D\u0435\u0446\u043A\u0438\u0439 \u0430\u0432\u0442\u043E\u043D\u043E\u043C\u043D\u044B\u0439 \u043E\u043A\u0440\u0443\u0433",
  "81": "\u0425\u0430\u043D\u0442\u044B-\u041C\u0430\u043D\u0441\u0438\u0439\u0441\u043A\u0438\u0439 \u0430\u0432\u0442\u043E\u043D\u043E\u043C\u043D\u044B\u0439 \u043E\u043A\u0440\u0443\u0433 \u2014 \u042E\u0433\u0440\u0430",
  "82": "\u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430 \u041A\u0440\u044B\u043C",
  "83": "\u0427\u0443\u043A\u043E\u0442\u0441\u043A\u0438\u0439 \u0430\u0432\u0442\u043E\u043D\u043E\u043C\u043D\u044B\u0439 \u043E\u043A\u0440\u0443\u0433",
  "84": "\u042F\u043C\u0430\u043B\u043E-\u041D\u0435\u043D\u0435\u0446\u043A\u0438\u0439 \u0430\u0432\u0442\u043E\u043D\u043E\u043C\u043D\u044B\u0439 \u043E\u043A\u0440\u0443\u0433",
  "85": "\u0421\u0435\u0432\u0430\u0441\u0442\u043E\u043F\u043E\u043B\u044C",
  "86": "\u0414\u043E\u043D\u0435\u0446\u043A\u0430\u044F \u041D\u0430\u0440\u043E\u0434\u043D\u0430\u044F \u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430",
  "87": "\u041B\u0443\u0433\u0430\u043D\u0441\u043A\u0430\u044F \u041D\u0430\u0440\u043E\u0434\u043D\u0430\u044F \u0420\u0435\u0441\u043F\u0443\u0431\u043B\u0438\u043A\u0430",
  "88": "\u0417\u0430\u043F\u043E\u0440\u043E\u0436\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "89": "\u0425\u0435\u0440\u0441\u043E\u043D\u0441\u043A\u0430\u044F \u043E\u0431\u043B\u0430\u0441\u0442\u044C",
  "99": "\u0411\u0430\u0439\u043A\u043E\u043D\u0443\u0440"
});
function regionTitle(code) {
  if (!code) return null;
  return RU_REGIONS[code] ?? code;
}
function countryTitle(code, locale = "ru") {
  if (!code) return null;
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

// src/constants/reminders.ts
var REMIND_DAYS_OPTIONS = [0, 1, 2, 3, 5, 7];
var DEFAULT_REMIND_DAYS = 3;

// src/constants/file-preview.ts
var BY_EXT = {
  png: { kind: "image", mime: "image/png" },
  jpg: { kind: "image", mime: "image/jpeg" },
  jpeg: { kind: "image", mime: "image/jpeg" },
  gif: { kind: "image", mime: "image/gif" },
  webp: { kind: "image", mime: "image/webp" },
  pdf: { kind: "pdf", mime: "application/pdf" },
  txt: { kind: "text", mime: "text/plain; charset=utf-8" },
  // Офисные документы показываются как PDF, который сервер собирает LibreOffice
  // (если он установлен); mime — тип того, что уходит в окно просмотра.
  doc: { kind: "office", mime: "application/pdf" },
  docx: { kind: "office", mime: "application/pdf" },
  odt: { kind: "office", mime: "application/pdf" },
  rtf: { kind: "office", mime: "application/pdf" },
  xls: { kind: "office", mime: "application/pdf" },
  xlsx: { kind: "office", mime: "application/pdf" },
  ods: { kind: "office", mime: "application/pdf" },
  ppt: { kind: "office", mime: "application/pdf" },
  pptx: { kind: "office", mime: "application/pdf" },
  odp: { kind: "office", mime: "application/pdf" }
};
function filePreview(fileName) {
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  return BY_EXT[ext] ?? null;
}

// src/constants/form-field-keys.ts
var FORM_FIELD_KEY_PATTERN = /^[a-z0-9_]+$/;
var FORM_FIELD_KEY_MAX = 64;
var WORK_REGION_ABROAD = "ABROAD";
var ISO_COUNTRY_CODES = Object.freeze(
  "AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ")
);
function countryOptions(locale = "ru") {
  const names = new Intl.DisplayNames([locale], { type: "region" });
  const ru = new Intl.DisplayNames(["ru"], { type: "region" });
  return ISO_COUNTRY_CODES.map((code) => ({
    value: code,
    label: names.of(code) ?? code,
    sortKey: ru.of(code) ?? code
  })).sort(
    (a, b) => a.value === "RU" ? -1 : b.value === "RU" ? 1 : a.sortKey.localeCompare(b.sortKey, "ru")
  ).map(({ value, label }) => ({ value, label }));
}
function workRegionOptions(locale = "ru") {
  const regions = Object.entries(RU_REGIONS).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, "ru"));
  return [
    ...regions,
    { value: WORK_REGION_ABROAD, label: locale === "en" ? "Outside Russia" : "\u0417\u0430 \u043F\u0440\u0435\u0434\u0435\u043B\u0430\u043C\u0438 \u0420\u0424" }
  ];
}
var SYSTEM_FIELD_KEYS = Object.freeze([
  {
    key: "country",
    label: "\u0421\u0442\u0440\u0430\u043D\u0430",
    labelEn: "Country",
    help: "\u0421\u0442\u0440\u0430\u043D\u0430, \u0438\u0437 \u043A\u043E\u0442\u043E\u0440\u043E\u0439 \u0432\u044B \u043F\u0440\u0438\u0435\u0445\u0430\u043B\u0438",
    helpEn: "The country you come from",
    type: "select",
    purpose: "\u0440\u0430\u0437\u0440\u0435\u0437 \u0443\u0447\u0430\u0441\u0442\u043D\u0438\u043A\u043E\u0432 \u043F\u043E \u0441\u0442\u0440\u0430\u043D\u0430\u043C \u0438 \u043A\u043E\u043B\u043E\u043D\u043A\u0430 \xAB\u0421\u0442\u0440\u0430\u043D\u0430\xBB \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440\u0430",
    labelPatterns: [/^\s*стран[аы](?!\p{L})/iu, /^\s*country(?!\p{L})/iu],
    options: countryOptions
  },
  {
    key: "work_region",
    label: "\u041C\u0435\u0441\u0442\u043E \u0440\u0430\u0431\u043E\u0442\u044B \u2014 \u0441\u0443\u0431\u044A\u0435\u043A\u0442 \u0420\u0424",
    labelEn: "Workplace \u2014 region of Russia",
    help: "\u0421\u0443\u0431\u044A\u0435\u043A\u0442 \u0420\u0424, \u0432 \u043A\u043E\u0442\u043E\u0440\u043E\u043C \u043D\u0430\u0445\u043E\u0434\u0438\u0442\u0441\u044F \u0432\u0430\u0448\u0435 \u043F\u0440\u0435\u0434\u043F\u0440\u0438\u044F\u0442\u0438\u0435. \u0415\u0441\u043B\u0438 \u043E\u043D\u043E \u0437\u0430 \u0440\u0443\u0431\u0435\u0436\u043E\u043C \u2014 \xAB\u0417\u0430 \u043F\u0440\u0435\u0434\u0435\u043B\u0430\u043C\u0438 \u0420\u0424\xBB",
    helpEn: "The region of Russia where your employer is located. If abroad, choose \u201COutside Russia\u201D",
    type: "select",
    purpose: "\u0440\u0430\u0437\u0440\u0435\u0437 \u0443\u0447\u0430\u0441\u0442\u043D\u0438\u043A\u043E\u0432 \u043F\u043E \u0440\u0435\u0433\u0438\u043E\u043D\u0430\u043C \u0438 \u043A\u043E\u043B\u043E\u043D\u043A\u0430 \xAB\u041C\u0435\u0441\u0442\u043E \u0440\u0430\u0431\u043E\u0442\u044B\xBB \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440\u0430",
    // «Регион» без уточнения сюда не относится: бывает «регион делегации» —
    // это не место работы, а у поля может быть свой тип `region`.
    labelPatterns: [/субъект\p{L}*\s+(рф|российской)/iu, /work\s*region/iu],
    options: workRegionOptions
  }
]);
function systemFieldKey(key) {
  return key ? SYSTEM_FIELD_KEYS.find((s) => s.key === key) : void 0;
}
function systemKeyForLabel(label) {
  if (!label) return void 0;
  return SYSTEM_FIELD_KEYS.find((s) => s.labelPatterns.some((p) => p.test(label)));
}

export { ACTIVITY_STATUSES, ACTIVITY_STATUS_LABELS_RU, ACTIVITY_TYPES, ACTIVITY_TYPE_LABELS_RU, ASSIGNMENT_SORT_KEYS, BADGE_CELL_MAX_MM, BADGE_CELL_MIN_MM, BADGE_TEXT_LINE_HEIGHT, BADGE_TEXT_MIN_SIZE, CONTENT_KINDS, CONTENT_KIND_LABELS_RU, DEFAULT_REMIND_DAYS, EVENT_LIFECYCLES, EVENT_SOURCES, EVENT_SOURCE_LABELS_RU, FORM_FIELD_KEY_MAX, FORM_FIELD_KEY_PATTERN, HEX_COLOR_RE, INTRANET_MODERATABLE_MODULES, INTRANET_MODULE_KEYS, INTRANET_MODULE_LABELS_RU, ISO_COUNTRY_CODES, LK_ADMIN_ROLE_CODE, LK_ADMIN_ROLE_REF, PASSWORD_MIN_LENGTH, PASSWORD_RULES_TEXT, PROJECT_COLORS, REGISTRATION_STATUSES, REGISTRATION_STATUS_LABELS_RU, REMIND_DAYS_OPTIONS, ROLE_LK_ADMIN, ROLE_LK_USER, RU_REGIONS, SERVICE_CATEGORIES, SERVICE_CATEGORY_LABELS_RU, SERVICE_VISIBILITIES, SERVICE_VISIBILITY_LABELS_RU, SYSTEM_FIELD_KEYS, WORK_REGION_ABROAD, announcementLetter, badgeCellGrid, bilingualLetter, birthDateRequestLetter, countryOptions, countryTitle, escapeAttr, escapeHtml, filePreview, findWindow, formatRoleRef, intakeInviteLetter, isAssignmentSortKey, isPersonalProjectColor, layoutBadgeText, parseRoleName, parseRoleRef, parseWindows, participantDecisionLetter, passwordIssues, passwordProblem, passwordResetLetter, pickBadgeCells, platformAdminLetter, regionTitle, registrationReceiptLetter, roleName, shrinkToWidth, sortAssignments, staffInviteLetter, systemFieldKey, systemKeyForLabel, workRegionOptions, zoneOffsetMinutes };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map